import type { ArtBox } from "@greg-chick/shared";

/**
 * 📐 **그림에서 실제로 그려진 사각형을 잰다** — 투명하지 않은 부분의 경계.
 *
 * ## 왜 브라우저에서 재나
 * 이 값을 재는 도구가 이미 있지만(`pnpm hero:anchors`) **개발자 PC의 저장소 파일**만 본다.
 * 운영자가 관리자에서 올린 그림은 영영 안 재진다 — 그래서 새 모자·소품은 자리가 안 맞았다
 * (2026-08-22 신고). 브라우저에서 재면 **올리는 그 자리에서** 바로 값이 나온다.
 *
 * ## 왜 알파 24 를 경계로 쓰나
 * 배경을 지운 그림은 가장자리에 **반투명 찌꺼기**가 남는다(안티에일리어싱·헤일로 제거 흔적).
 * 0 을 경계로 하면 그 찌꺼기까지 물건으로 세어 사각형이 실제보다 커지고, 그만큼 물건이
 * 작게 놓인다. 24 는 `hero-anchors` 스크립트가 쓰는 값과 같다 — 두 도구가 다른 답을 내면 안 된다.
 */
const ALPHA_MIN = 24;

/** 너무 큰 그림을 그대로 재면 느리다. 사각형은 비율이라 줄여도 값이 거의 같다. */
const MAX_SIDE = 512;

/**
 * 이미지 주소 → 그려진 사각형(프레임 대비 비율). 못 재면 null.
 *
 * 다른 출처의 그림도 재려면 CORS 가 필요하다 — 우리 엣지는 허용돼 있다.
 * 실패하면 **null 을 돌려주고 화면이 "잴 수 없어요"라고 말한다.** 조용히 0을 주면
 * 운영자는 자동 맞춤이 동작한 줄 알고 이상한 자리를 그대로 저장한다.
 */
export async function measureArtBox(url: string): Promise<ArtBox | null> {
  const img = await loadImage(url);
  if (!img) return null;
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (!w || !h) return null;

  const k = Math.min(1, MAX_SIDE / Math.max(w, h));
  const cw = Math.max(1, Math.round(w * k));
  const ch = Math.max(1, Math.round(h * k));
  const c = document.createElement("canvas");
  c.width = cw;
  c.height = ch;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, cw, ch);

  let data: Uint8ClampedArray;
  try {
    data = ctx.getImageData(0, 0, cw, ch).data;
  } catch {
    return null; // 다른 출처 그림이 CORS 없이 그려졌다 — 캔버스가 오염돼 읽을 수 없다
  }

  let x0 = cw;
  let y0 = ch;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < ch; y++) {
    for (let x = 0; x < cw; x++) {
      if (data[(y * cw + x) * 4 + 3]! <= ALPHA_MIN) continue;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
    }
  }
  if (x1 < 0) return { x: 0, y: 0, w: 0, h: 0 }; // 전부 투명 — 빈 그림이라고 알려 준다
  return { x: x0 / cw, y: y0 / ch, w: (x1 - x0 + 1) / cw, h: (y1 - y0 + 1) / ch };
}

function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous"; // 안 붙이면 캔버스가 오염돼 픽셀을 못 읽는다
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}
